import SpotifyAPI from 'spotify-web-api-js';
import { getDB, officialList, Track } from './core';

import LonglistLogic from './longlist';

const spotifyAPI = new SpotifyAPI();
const CLIENT_ID = 'df693d57bc374ec0a2b558650c56b324';

const ACCESS_TOKEN_KEY = 'spotify_access_token';
const PLAYLIST_ID_KEY = 'spotify_playlist_id_2025';
const STATE_KEY = '__temp_spotify_state';
const CODE_VERIFIER_KEY = '__temp_spotify_code_verifier';
const REDIRECT_KEY = '__temp_spotify_redirect';
const ACTION_KEY = '__temp_spotify_action';
const PARAMS_KEY = '__temp_spotify_params';

const BASE_URL = process.env.REACT_APP_BASE_URL;

interface SpotifyAccessTokenResult {
  access_token: string
  expires_in: number
  refresh_token: string
}

interface SpotifyAccessTokenError {
  error: string
  error_description: string
}

type SpotifyAccessTokenResponse = SpotifyAccessTokenResult | SpotifyAccessTokenError;

function base64URLSafe(input: ArrayBuffer): string {
  return window.btoa(String.fromCharCode(...new Uint8Array(input)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}

function randomString(length: number): string {
  const randomBuffer = crypto.getRandomValues(new Uint8Array(Math.ceil(length / 4 * 3)));
  return base64URLSafe(randomBuffer.buffer).slice(0, length);
}

async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const buf = encoder.encode(input);
  const digest = await window.crypto.subtle.digest('SHA-256', buf);
  return base64URLSafe(digest);
}

interface SpotifyActions {
  replaceShortlistWithPlaylist(playlistId: string): Promise<void>
  updatePlaylistFromShortlist(): Promise<void>
}

type SpotifyPasteboardData = {
  PLAYLIST_ID_KEY: string | null
}

class Spotify implements SpotifyActions {

  get isLoggedIn(): boolean {
    let accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (accessToken) {
      spotifyAPI.setAccessToken(accessToken);
      return true;
    }
    return false;
  }

  get isPlaylistCreated(): boolean {
    return localStorage.getItem(PLAYLIST_ID_KEY) !== null;
  }

  clearToken() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    spotifyAPI.setAccessToken(null);
  }

  copyData(): SpotifyPasteboardData {
    return {
      PLAYLIST_ID_KEY: localStorage.getItem(PLAYLIST_ID_KEY)
    }
  }

  pasteData(data: SpotifyPasteboardData) {
    if (data.PLAYLIST_ID_KEY) {
      localStorage.setItem(PLAYLIST_ID_KEY, data.PLAYLIST_ID_KEY);
    }
  }

  get redirectUri(): string {
    let redirect = new URL('./spotify_redirect', BASE_URL);
    return redirect.toString();
  }

  async login() {
    const state = randomString(16);
    const codeVerifier = randomString(128);
    const codeChallenge = await sha256(codeVerifier);

    localStorage.setItem(STATE_KEY, state);
    localStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

    let url = new URL('https://accounts.spotify.com/authorize');
    url.searchParams.set('client_id', CLIENT_ID);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', this.redirectUri);
    url.searchParams.set('scope', 'playlist-modify-private');
    url.searchParams.set('state', state);
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('code_challenge', codeChallenge);

    window.location.href = url.toString();
  }

  async performAction<K extends keyof SpotifyActions>(action: K, ...params: Parameters<SpotifyActions[K]>) {
    if (this.isLoggedIn) {
      try {
        // @ts-ignore: 2684
        await this[action](...params);
        return;
      } catch (e) {
        if (e instanceof XMLHttpRequest) {
          if (e.status === 401) {
            this.clearToken();
          } else {
            const error = JSON.parse(e.responseText);
            throw error.error.message;
          }
        } else {
          throw e;
        }
      }

    }

    // Otherwise, do a login
    localStorage.setItem(REDIRECT_KEY, window.location.pathname);
    localStorage.setItem(ACTION_KEY, action);
    localStorage.setItem(PARAMS_KEY, JSON.stringify(params));

    await this.login();
  }

  async getToken(code: string, codeVerifier: string): Promise<SpotifyAccessTokenResponse> {
    const url = "https://accounts.spotify.com/api/token";
    const payload = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.redirectUri,
        code_verifier: codeVerifier,
      }),
    }

    try {
      const response = await fetch(url, payload);

      if (response.ok) {
        return await response.json() as SpotifyAccessTokenResult;
      } else {
        return await response.json() as SpotifyAccessTokenError;
      }

    } catch (err) {
      debugger;
      console.error(err);
      throw err;
    }
  }

  async handleRedirect(result: URLSearchParams) {
    try {
      const checkState = localStorage.getItem(STATE_KEY);
      const code = result.get('code');
      const state = result.get('state');
      const error = result.get('error');

      const redirect = localStorage.getItem(REDIRECT_KEY);
      const action = localStorage.getItem(ACTION_KEY);
      const params = JSON.parse(localStorage.getItem(PARAMS_KEY) ?? '[]');

      const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY);
      if (!codeVerifier) {
        // No code verifier - we probably just refreshed the page
        // throw new Error('Out-of-sequence - no code verifier stored');
        return '/';
      }

      if (state !== checkState) {
        // Wrong state, just return us to the top level
        throw new Error('State is wrong, request probably forged');
      }

      if (error) {
        throw new Error(error);
      }

      if (!code) {
        throw new Error('No code in redirect URI');
      }

      const token = await this.getToken(code, codeVerifier);

      // TODO: check if anything else went wrong
      if ('access_token' in token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, token.access_token);
        spotifyAPI.setAccessToken(token.access_token);
        // Perform the action we had waiting

        // @ts-ignore: 2684
        await this[action](...params);

        return redirect;
      } else {
        throw new Error(token.error_description) ?? 'unknown error';
      }
    } finally {
      localStorage.removeItem(CODE_VERIFIER_KEY);
      localStorage.removeItem(REDIRECT_KEY);
      localStorage.removeItem(ACTION_KEY);
      localStorage.removeItem(PARAMS_KEY);
    }
  }

  async replaceShortlistWithPlaylist(playlistId: string) {
    const longlistLogic = new LonglistLogic();

    let playlistTracks: string[] = [];
    while(true) {
      const response = await spotifyAPI.getPlaylistTracks(playlistId, {fields: 'total,items.track.id', limit: 50, offset: playlistTracks.length});
      playlistTracks = playlistTracks.concat(response.items.map(i => i.track.id));
      if (response.items.length === 0) {
        break;
      }
    }

    await longlistLogic.resetLonglist('reject');

    let officialListBySpotifyId = new Map<string, Track>(Object.values(officialList)
        .filter(t => t.spotify_track_id !== null)
        .map(t => [t.spotify_track_id!, t]));

    for(let spotifyId of playlistTracks) {
      const track = officialListBySpotifyId.get(spotifyId);
      if (track) {
        await longlistLogic.updateState(track, 'accept');
      }
    }
  }

  async updatePlaylistFromShortlist() {
    const db = await getDB();
    const longlist = await db.transaction('longlist').store;

    const entries = await longlist.index('state').getAll('accept');
    const tracks = entries.map(e => officialList[e.id.toString()]);
    tracks.sort((a, b) => a.artist_name < b.artist_name ? -1 : (a.artist_name > b.artist_name ? 1 : 0))
    const uris = tracks.filter(o => o.spotify_track_id != null)
      .map(o => `spotify:track:${o.spotify_track_id}`);

    let playlistId = localStorage.getItem(PLAYLIST_ID_KEY);

    if (!playlistId) {
      const user = await spotifyAPI.getMe();
      const playlist = await spotifyAPI.createPlaylist(user.id, {
        name: 'Hottest 100 Helper 2025',
        public: false
      });
      playlistId = playlist.id;
      localStorage.setItem(PLAYLIST_ID_KEY, playlistId);
    }

    // Chunk into 100 track size chunks
    await spotifyAPI.replaceTracksInPlaylist(playlistId, []);

    for(let i = 0; i < uris.length; i += 100) {
      let _uris = uris.slice(i, i + 100);
      await spotifyAPI.addTracksToPlaylist(playlistId, _uris);
    }

    let problemTracks = tracks
      .filter(o => o.spotify_track_id == null)
      .map(o => `${o.artist_name.toUpperCase()} – ${o.title}`)
      .join('\n');

    if (problemTracks !== '') {
      problemTracks = "\n\nCouldn't add these tracks:\n" + problemTracks;
    }

    alert(`Created playlist "Hottest 100 Helper" in Spotify${problemTracks}`);

  }
}

export default new Spotify();
